/**
 * Lazily loaded Firestore SDK.
 *
 * `firebase/firestore` is the largest piece of startup JavaScript (~555 KB), and anonymous
 * visitors on the main public pages never use it. Code on the startup path imports Firestore
 * functions from this module instead of from 'firebase/firestore'.
 *
 * The exports are live bindings. Until the SDK is loaded they throw a descriptive error (and start
 * the load, so a retry works). `$firebase.ensureFirestore()` (plugins/01.firebase.client.ts) loads the
 * SDK and rebinds them to the real functions. Signed-in users always have it loaded, because the auth
 * flow awaits it, and middleware/firestore.global.ts loads it before every route that isn't on its
 * anonymous allowlist.
 */
import type * as FirestoreSdk from 'firebase/firestore'

type Sdk = typeof FirestoreSdk

let onUseBeforeLoad: (() => void) | undefined

function notLoadedError(name: string): Error {
    onUseBeforeLoad?.()
    const error = new Error(
        `[firebase] Firestore "${name}" was used before the SDK was loaded. ` +
            'Await $firebase.ensureFirestore() first, or take the route off the anonymous allowlist in middleware/firestore.global.ts.'
    )
    // Callers often catch and swallow errors; make sure this one is seen during development.
    if (import.meta.dev) console.error(error)
    return error
}

/**
 * Placeholder for an SDK export. Calling it, constructing it or reading a static member
 * (`Timestamp.now()`) throws. `instanceof` still works and is always false, since no Firestore
 * object can exist before the SDK is loaded.
 */
function pending<K extends keyof Sdk>(name: K): Sdk[K] {
    return new Proxy(function () {}, {
        apply() {
            throw notLoadedError(name)
        },
        construct() {
            throw notLoadedError(name)
        },
        get(target, prop, receiver) {
            if (typeof prop === 'symbol' || prop === 'prototype') {
                return Reflect.get(target, prop, receiver)
            }
            throw notLoadedError(`${name}.${prop}`)
        }
    }) as unknown as Sdk[K]
}

export let addDoc = pending('addDoc')
export let arrayRemove = pending('arrayRemove')
export let arrayUnion = pending('arrayUnion')
export let collection = pending('collection')
export let connectFirestoreEmulator = pending('connectFirestoreEmulator')
export let doc = pending('doc')
export let endBefore = pending('endBefore')
export let getDoc = pending('getDoc')
export let getDocs = pending('getDocs')
export let initializeFirestore = pending('initializeFirestore')
export let limit = pending('limit')
export let onSnapshot = pending('onSnapshot')
export let orderBy = pending('orderBy')
export let query = pending('query')
export let serverTimestamp = pending('serverTimestamp')
export let setDoc = pending('setDoc')
export let startAfter = pending('startAfter')
export let updateDoc = pending('updateDoc')
export let where = pending('where')

// Classes: exported as a value (live binding) and as a type, like the SDK does.
type Data = FirestoreSdk.DocumentData
export let DocumentReference = pending('DocumentReference')
export type DocumentReference<A = Data, D extends Data = Data> = FirestoreSdk.DocumentReference<A, D>
export let Query = pending('Query')
export type Query<A = Data, D extends Data = Data> = FirestoreSdk.Query<A, D>
export let Timestamp = pending('Timestamp')
export type Timestamp = FirestoreSdk.Timestamp

let sdkPromise: Promise<Sdk> | undefined

/** Loads the SDK once and rebinds every export above to the real implementation. */
export function loadFirestoreSdk(): Promise<Sdk> {
    sdkPromise ??= import('firebase/firestore')
        .then((sdk) => {
            addDoc = sdk.addDoc
            arrayRemove = sdk.arrayRemove
            arrayUnion = sdk.arrayUnion
            collection = sdk.collection
            connectFirestoreEmulator = sdk.connectFirestoreEmulator
            doc = sdk.doc
            endBefore = sdk.endBefore
            getDoc = sdk.getDoc
            getDocs = sdk.getDocs
            initializeFirestore = sdk.initializeFirestore
            limit = sdk.limit
            onSnapshot = sdk.onSnapshot
            orderBy = sdk.orderBy
            query = sdk.query
            serverTimestamp = sdk.serverTimestamp
            setDoc = sdk.setDoc
            startAfter = sdk.startAfter
            updateDoc = sdk.updateDoc
            where = sdk.where
            DocumentReference = sdk.DocumentReference
            Query = sdk.Query
            Timestamp = sdk.Timestamp
            return sdk
        })
        .catch((error) => {
            sdkPromise = undefined
            throw error
        })
    return sdkPromise
}

/** Lets the Firebase plugin start loading (and initialising) Firestore when a placeholder is hit. */
export function onFirestoreUsedBeforeLoad(handler: () => void): void {
    onUseBeforeLoad = handler
}
